import { Project } from '../types';

/**
 * Converts a string to kebab-case
 * Handles special characters, spaces, and maintains readability
 */
export function kebabCase(str: string): string {
  return str
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters except word chars, spaces, and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Generates a project slug from project name and ID
 * Format: kebab-case(projectName)-{last8CharsOfId}
 * Example: "brand-redesign-acme-abc12345"
 */
export function generateProjectSlug(project: Project): string {
  const nameSlug = kebabCase(project.name);
  const idSuffix = project.id.slice(-8); // Last 8 characters for uniqueness
  return `${nameSlug}-${idSuffix}`;
}

/**
 * Parses a project slug to extract the ID suffix
 * Returns an object with the name part and ID suffix
 */
export function parseProjectSlug(slug: string): { name: string; idSuffix: string } {
  const parts = slug.split('-');
  if (parts.length < 2) {
    // Fallback: treat entire slug as ID suffix if no hyphen found
    return { name: '', idSuffix: slug };
  }
  
  // Last part should be the 8-character ID suffix
  const idSuffix = parts[parts.length - 1];
  const nameParts = parts.slice(0, -1);
  const name = nameParts.join('-');
  
  return { name, idSuffix };
}

/**
 * Finds a project by its slug
 * Matches projects by comparing the last 8 characters of the ID
 */
export function findProjectBySlug(slug: string, projects: Project[]): Project | undefined {
  const { idSuffix } = parseProjectSlug(slug);
  return projects.find(p => p.id.endsWith(idSuffix));
}
